import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useDrawerStore } from '@/stores/drawerStore';
import { useAuthStore } from '@/stores/authStore';
import { validateWebsiteUrl } from '@/schemas/websiteSchema';
import { emailSchema } from '@/schemas/formDataSchema';

/**
 * フィールドエラーの型
 */
interface FieldErrors {
  websiteUrl?: string;
  companyName?: string;
  contactName?: string;
  email?: string;
}

/**
 * UrlInputFormView
 * URL 入力 + お問い合わせフォーム
 * ログイン済みユーザーは情報が自動入力される
 */
export function UrlInputFormView() {
  // Zustand のアクションは安定した参照を持つため、個別にセレクト
  const formData = useDrawerStore((state) => state.formData);
  const setFormData = useDrawerStore((state) => state.setFormData);
  const setActiveView = useDrawerStore((state) => state.setActiveView);
  const { user, isAuthenticated } = useAuthStore();
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // ユーザー情報の自動入力が完了したかを追跡
  // user オブジェクト参照が変わっても再実行しないようにするため
  const hasPopulatedUserData = useRef(false);

  // ログイン済みユーザーの情報を自動入力（一度だけ実行）
  // 注意: OAuth サーバーが name を返さない場合があるため、
  //       空の値でユーザー入力を上書きしないように注意
  useEffect(() => {
    // 既に自動入力済みの場合はスキップ
    if (hasPopulatedUserData.current) {
      return;
    }

    if (isAuthenticated && user) {
      // email は常に設定、contactName は値がある場合のみ設定
      // user.name が null/undefined/空文字の場合は、ユーザーが入力した値を保持
      const updates: Partial<typeof formData> = {
        email: user.email,
      };
      if (user.name) {
        updates.contactName = user.name;
      }
      setFormData(updates);
      hasPopulatedUserData.current = true;
    }
  }, [isAuthenticated, user, setFormData]);

  // フォーム送信
  // Zustand ストアから直接値を取得（controlled component なので最も信頼性が高い）
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Zustand ストアから直接取得（controlled component の値は常に最新）
    const currentFormData = useDrawerStore.getState().formData;

    const submittedData = {
      websiteUrl: currentFormData.websiteUrl,
      companyName: currentFormData.companyName,
      contactName: currentFormData.contactName,
      email: currentFormData.email,
    };

    // バリデーション（フォームから取得した値を使用）
    const errors: FieldErrors = {};

    const urlResult = validateWebsiteUrl(submittedData.websiteUrl);
    if (!urlResult.success) {
      errors.websiteUrl = urlResult.error;
    }

    if (!submittedData.companyName.trim()) {
      errors.companyName = '会社名を入力してください';
    }

    if (!submittedData.contactName.trim()) {
      errors.contactName = '氏名を入力してください';
    }

    const emailResult = emailSchema.safeParse(submittedData.email);
    if (!emailResult.success) {
      errors.email = emailResult.error.issues[0]?.message ?? '有効なメールアドレスを入力してください';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // エラーをクリア
    setFieldErrors({});

    // 診断フォームに遷移（API送信・GA4イベントはDiagnosticFormViewで行う）
    setActiveView('diagnostic');
  };

  // フィールド更新（エラーをクリア）
  const handleChange = (
    field: keyof typeof formData,
    value: string
  ) => {
    setFormData({ [field]: value });
    // 該当フィールドのエラーをクリア
    if (fieldErrors[field as keyof FieldErrors]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // 必須フィールドがすべて入力されているかチェック
  // 送信ボタンの disabled 状態に使用
  const isFormComplete =
    formData.websiteUrl.trim() !== '' &&
    formData.companyName.trim() !== '' &&
    formData.contactName.trim() !== '' &&
    formData.email.trim() !== '';

  return (
    <>
      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-[#111827] mb-2 text-xl sm:text-2xl lg:text-[28px] font-bold leading-tight">
            無料提案のご依頼
          </h2>
          <p className="text-[#6B7280] text-sm sm:text-base">
            {isAuthenticated
              ? 'ご登録情報が自動入力されています。内容をご確認の上、送信してください。'
              : '以下のフォームにご入力いただくと、貴社に最適な広告プランを無料でご提案いたします。'}
          </p>
        </div>

        <form id="contact-form" onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {/* Website URL */}
          <div>
            <label
              htmlFor="form-website-url"
              className="block text-[#111827] mb-1.5 sm:mb-2 text-xs sm:text-sm font-semibold"
            >
              提案対象のURL <span className="text-red-500">*</span>
            </label>
            <input
              id="form-website-url"
              name="form-website-url"
              type="text"
              required
              value={formData.websiteUrl}
              onChange={(e) => handleChange('websiteUrl', e.target.value)}
              aria-invalid={fieldErrors.websiteUrl ? 'true' : 'false'}
              aria-describedby={fieldErrors.websiteUrl ? 'form-url-error' : undefined}
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-2 rounded-lg text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none transition-colors text-sm ${
                fieldErrors.websiteUrl
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-[#E5E7EB] focus:border-[#0264BF]'
              }`}
              placeholder="https://www.example.com"
            />
            {fieldErrors.websiteUrl && (
              <p id="form-url-error" role="alert" className="text-red-600 mt-1 text-xs sm:text-sm font-medium">
                {fieldErrors.websiteUrl}
              </p>
            )}
          </div>

          {/* Company Name */}
          <div>
            <label
              htmlFor="form-company-name"
              className="block text-[#111827] mb-1.5 sm:mb-2 text-xs sm:text-sm font-semibold"
            >
              会社名 <span className="text-red-500">*</span>
            </label>
            <input
              id="form-company-name"
              name="form-company-name"
              type="text"
              required
              value={formData.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              aria-invalid={fieldErrors.companyName ? 'true' : 'false'}
              aria-describedby={fieldErrors.companyName ? 'form-company-error' : undefined}
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-2 rounded-lg text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none transition-colors text-sm ${
                fieldErrors.companyName
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-[#E5E7EB] focus:border-[#0264BF]'
              }`}
              placeholder="例：株式会社ラクスル"
            />
            {fieldErrors.companyName && (
              <p id="form-company-error" role="alert" className="text-red-600 mt-1 text-xs sm:text-sm font-medium">
                {fieldErrors.companyName}
              </p>
            )}
          </div>

          {/* Contact Name */}
          <div>
            <label
              htmlFor="form-contact-name"
              className="block text-[#111827] mb-1.5 sm:mb-2 text-xs sm:text-sm font-semibold"
            >
              氏名 <span className="text-red-500">*</span>
            </label>
            <input
              id="form-contact-name"
              name="form-contact-name"
              type="text"
              required
              value={formData.contactName}
              onChange={(e) => handleChange('contactName', e.target.value)}
              aria-invalid={fieldErrors.contactName ? 'true' : 'false'}
              aria-describedby={fieldErrors.contactName ? 'form-contact-error' : undefined}
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-2 rounded-lg text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none transition-colors text-sm ${
                fieldErrors.contactName
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-[#E5E7EB] focus:border-[#0264BF]'
              }`}
              placeholder="例：山田 太郎"
            />
            {fieldErrors.contactName && (
              <p id="form-contact-error" role="alert" className="text-red-600 mt-1 text-xs sm:text-sm font-medium">
                {fieldErrors.contactName}
              </p>
            )}
          </div>

          {/* Email */}
          <div>
            <label
              htmlFor="form-email"
              className="block text-[#111827] mb-1.5 sm:mb-2 text-xs sm:text-sm font-semibold"
            >
              メールアドレス <span className="text-red-500">*</span>
            </label>
            <input
              id="form-email"
              name="form-email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              aria-invalid={fieldErrors.email ? 'true' : 'false'}
              aria-describedby={fieldErrors.email ? 'form-email-error' : undefined}
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-2 rounded-lg text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none transition-colors text-sm ${
                fieldErrors.email
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-[#E5E7EB] focus:border-[#0264BF]'
              }`}
              placeholder="例：yamada@example.com"
            />
            {fieldErrors.email && (
              <p id="form-email-error" role="alert" className="text-red-600 mt-1 text-xs sm:text-sm font-medium">
                {fieldErrors.email}
              </p>
            )}
          </div>

        </form>
      </div>

      {/* Fixed Footer with Submit Button */}
      <div className="border-t border-[#E5E7EB] bg-[#F9FAFB] p-4 sm:p-6">
        <Button
          type="submit"
          form="contact-form"
          disabled={!isFormComplete}
          className="w-full bg-[#0052FF] hover:bg-[#0041CC] text-white rounded-lg transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base font-bold h-11 sm:h-12"
        >
          次に進む
        </Button>
      </div>
    </>
  );
}
